import { useQuery, useMutation, useQueryClient, UseMutationResult, UseQueryResult, UseQueryOptions } from "@tanstack/react-query";
import { deleteContactForms, listContactForms } from "./callback";
import { IContactApiResponse } from "../../../types/api/contact";
import { QData } from "../../../types/basic-type-list";
import { TResponseWithQData, TResponse } from "../../../types/common";
import { useState } from "react";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";

type ContactListQueryOptions = Omit<
  UseQueryOptions<TResponseWithQData<IContactApiResponse[]>, Error>,
  "queryKey" | "queryFn"
>;

export const useContactList = (
  formType: "CONTACT" | "ENQUIRY",
  initialQData: QData = { page: 1, limit: 20 },
  options?: ContactListQueryOptions
): UseQueryResult<TResponseWithQData<IContactApiResponse[]>, Error> & {
  refetchWithQuery: (params: Partial<QData>) => void;
  qData: QData;
} => {
  const [qData, setQData] = useState<QData>({ ...initialQData });

  const queryResult = useQuery<TResponseWithQData<IContactApiResponse[]>, Error>({
    queryKey: [API_ENDPOINTS.LIST_CONTACT, formType, qData],

    queryFn: async () => {
      const res = await listContactForms(qData, formType);
      return {
        ...res.data,
        qData: {
          ...qData,
          total: res.data.extra?.total ?? 0,
        },
      };
    },

    staleTime: 300000,
    refetchOnWindowFocus: false,
    ...options,
  });

  return {
    ...queryResult,
    qData,

    refetchWithQuery: (params: Partial<QData>) => {
      setQData((prev) => ({
        ...prev,
        ...params,
      }));
    },
  };
};


export const useDeleteContactForm = (): UseMutationResult<
  TResponse<null>,
  Error,
  string
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await deleteContactForms(id);
      return res.data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.LIST_CONTACT],
      });
    },
  });
};